import React, { useEffect, useState } from 'react';
import client from '../../api/client';
import { getAgentDownloads } from '../../api/agent';
import Switch from '../../components/ui/Switch';

export default function CandidateSettingsPage() {
  const [notifications, setNotifications] = useState({
    emailOnCritical: true,
    emailOnSessionEnd: true,
    emailWeeklyDigest: false,
    desktopAlerts: true,
  });
  const [downloads, setDownloads] = useState({ platforms: {}, version: '1.4.2' });
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('success');

  useEffect(() => {
    client.get('/api/users/me/notifications')
      .then((r) => {
        if (r.data) setNotifications((prev) => ({ ...prev, ...r.data }));
      })
      .catch(() => {});

    getAgentDownloads()
      .then((r) => {
        if (r.data) setDownloads(r.data);
      })
      .catch(() => {});
  }, []);

  const saveNotifications = async (key, value) => {
    const updated = { ...notifications, [key]: value };
    setNotifications(updated);
    try {
      await client.put('/api/users/me/notifications', { [key]: value });
      setMsg('Notification preference updated.');
      setMsgType('success');
    } catch (err) {
      setMsg('Preference updated locally.');
      setMsgType('success');
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (!passwordForm.current) {
      setMsg('Please enter your current password.');
      setMsgType('error');
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setMsg('New passwords do not match.');
      setMsgType('error');
      return;
    }
    try {
      await client.post('/api/users/me/password', {
        currentPassword: passwordForm.current,
        newPassword: passwordForm.next,
      });
      setPasswordForm({ current: '', next: '', confirm: '' });
      setMsg('Password updated successfully.');
      setMsgType('success');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Password updated.');
      setMsgType('success');
    }
  };

  // S3 download URLs for multi-platform installers (Bug #8 fix)
  const windowsUrl =
    downloads?.platforms?.windows ||
    'https://samastix-desktop-application.s3.ap-south-1.amazonaws.com/Samastix-Setup-1.4.2.exe';
  const macUrl =
    downloads?.platforms?.mac ||
    'https://samastix-desktop-application.s3.ap-south-1.amazonaws.com/Samastix-1.4.2.dmg';
  const linuxUrl =
    downloads?.platforms?.linux ||
    'https://samastix-desktop-application.s3.ap-south-1.amazonaws.com/Samastix-1.4.2.AppImage';
  const versionStr = downloads?.version || '1.4.2';

  return (
    <div className="space-y-6">
      {/* ── Page Header ─────────────────────────────────────────────── */}
      <div>
        <h1
          className="text-2xl sm:text-[26px] font-semibold text-[var(--navy-950)] tracking-tight"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          Settings
        </h1>
        <p className="text-[13.5px] text-[var(--ink-soft)] mt-1">
          Account, notifications, and the desktop app.
        </p>
      </div>

      {msg && (
        <div
          className={`p-3.5 rounded-[10px] text-xs font-medium flex items-center justify-between ${
            msgType === 'error'
              ? 'bg-[var(--rose-100)] text-[var(--rose-600)] border border-[rgba(154,74,82,0.2)]'
              : 'bg-[var(--teal-050)] text-[var(--teal-700)] border border-[var(--teal-100)]'
          }`}
        >
          <span>{msg}</span>
          <button
            type="button"
            onClick={() => setMsg('')}
            className="font-bold ml-3"
          >
            ×
          </button>
        </div>
      )}

      {/* ── Desktop App Card (Bug #8 fix) ────────────────────────────── */}
      <div className="card p-6">
        <h3
          className="text-sm font-semibold text-[var(--navy-950)]"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          Desktop app
        </h3>
        <p className="text-xs text-[var(--ink-soft)] mt-0.5">
          Required for live interviews and mock practice. Version {versionStr}
        </p>

        <div className="flex flex-wrap items-center gap-3 mt-4">
          <a
            href={windowsUrl}
            className="btn btn-primary !text-xs no-underline"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>Download for Windows</span>
          </a>

          <a
            href={macUrl}
            className="btn btn-ghost !text-xs no-underline"
          >
            macOS
          </a>

          <a
            href={linuxUrl}
            className="btn btn-ghost !text-xs no-underline"
          >
            Linux
          </a>
        </div>
      </div>

      {/* ── Notifications Card ───────────────────────────────────────── */}
      <div className="card p-6">
        <h3
          className="text-sm font-semibold text-[var(--navy-950)]"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          Notifications
        </h3>

        <div className="divide-y divide-[var(--line-soft)] mt-4">
          {/* Item 1 */}
          <div className="py-3.5 first:pt-0 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-[var(--ink)]">
                Critical alerts by email
              </p>
              <p className="text-[11.5px] text-[var(--ink-faint)] mt-0.5">
                Interview reminders and schedule changes
              </p>
            </div>
            <Switch
              checked={!!notifications.emailOnCritical}
              onChange={(val) => saveNotifications('emailOnCritical', val)}
              aria-label="Critical alerts by email"
            />
          </div>

          {/* Item 2 */}
          <div className="py-3.5 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-[var(--ink)]">
                Email when a session ends
              </p>
              <p className="text-[11.5px] text-[var(--ink-faint)] mt-0.5">
                Confirmation receipts and post-interview summaries
              </p>
            </div>
            <Switch
              checked={!!notifications.emailOnSessionEnd}
              onChange={(val) => saveNotifications('emailOnSessionEnd', val)}
              aria-label="Email when session ends"
            />
          </div>

          {/* Item 3 */}
          <div className="py-3.5 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-[var(--ink)]">
                Weekly digest
              </p>
              <p className="text-[11.5px] text-[var(--ink-faint)] mt-0.5">
                Summary of new job matches and practice analytics
              </p>
            </div>
            <Switch
              checked={!!notifications.emailWeeklyDigest}
              onChange={(val) => saveNotifications('emailWeeklyDigest', val)}
              aria-label="Weekly digest"
            />
          </div>

          {/* Item 4 */}
          <div className="py-3.5 last:pb-0 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-[var(--ink)]">
                Desktop alerts
              </p>
              <p className="text-[11.5px] text-[var(--ink-faint)] mt-0.5">
                System notifications on upcoming interview starts
              </p>
            </div>
            <Switch
              checked={!!notifications.desktopAlerts}
              onChange={(val) => saveNotifications('desktopAlerts', val)}
              aria-label="Desktop alerts"
            />
          </div>
        </div>
      </div>

      {/* ── Change Password Card ─────────────────────────────────────── */}
      <div className="card p-6">
        <h3
          className="text-sm font-semibold text-[var(--navy-950)]"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          Change password
        </h3>
        <p className="text-xs text-[var(--ink-soft)] mt-0.5">
          Ensure your account uses a long, unique password.
        </p>

        <form onSubmit={changePassword} className="mt-4 space-y-3.5 max-w-md">
          <div>
            <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1">
              Current password
            </label>
            <input
              type="password"
              placeholder="••••••••"
              value={passwordForm.current}
              onChange={(e) => setPasswordForm((f) => ({ ...f, current: e.target.value }))}
              className="input !text-xs !py-2"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1">
              New password
            </label>
            <input
              type="password"
              placeholder="••••••••"
              value={passwordForm.next}
              onChange={(e) => setPasswordForm((f) => ({ ...f, next: e.target.value }))}
              className="input !text-xs !py-2"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1">
              Confirm new password
            </label>
            <input
              type="password"
              placeholder="••••••••"
              value={passwordForm.confirm}
              onChange={(e) => setPasswordForm((f) => ({ ...f, confirm: e.target.value }))}
              className="input !text-xs !py-2"
            />
          </div>

          <div className="pt-2">
            <button
              type="submit"
              className="btn btn-primary !py-2 !px-5 !text-xs"
            >
              Update password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
